import React, { useEffect } from "react";
import { Card, CardBody, Row } from "reactstrap";
import { yupResolver } from "@hookform/resolvers";
import { useForm, FormProvider } from "react-hook-form";
import shallow from "zustand/shallow";
import useCurrentPhotoStore from "stores/PhotoStore";
import useCurrentVehicleStore from "stores/InventoryStore";
import { MultiImageProvider } from "features/inventory/subFeatures/image/multi/MultiImageContext";
import { getAllVendorSortedByLocationID } from "api/VendorsAPI";
import { useFromUser } from "stores/LocalStorageHelper";
import { showApiError } from "utils/errorRoutingUtils";
import { retrieveFailAlert } from "utils/alertUtils";
import dayjs from "dayjs";
import VinDecoder from "features/vinDecoder/components/VinDecoder";
import InventoryDetailsTab from "../subFeatures/inventoryTabs/detailsTab/InventoryDetailsTab";
import UploadPhotos from "../subFeatures/inventoryTabs/photosTab/UploadPhotos";
import { KbbValuesProvider } from "../subFeatures/inventoryTabs/valuesTab/KbbContext";
import { NewInventorySaveButton } from "../helperFunctions/InventorySaveButton";
import { inventoryValidationSchema } from "../validation/inventoryValidationSchema";

const InventoryAddPage = () => {
	const locationID = useFromUser("locationID");
	const firstName = useFromUser("firstName");
	const lastName = useFromUser("lastName");
	const dcLoginID = useFromUser("ID");
	const today = dayjs().format("YYYY-MM-DD");
	const inventorySettings = JSON.parse(
		sessionStorage.getItem("inventoryDefaults")
	);
	const {
		reset,
		editType,
		editIsLoadingFalse: dataIsLoaded,
		editVendorsList,
		editReconVendorsList,
		editFlooringVendorsList,
		editAllVendorList,
		editTradeLenderList,
		editModifiedOn,
		editModifiedByID,
		editOpenedBy,
	} = useCurrentVehicleStore((state) => state, shallow);

	// Photo store
	const { resetPhotos } = useCurrentPhotoStore(
		(state) => ({ resetPhotos: state.reset }),
		shallow
	);

	// Gets all the inventory related details
	const getInvDetails = () => {
		const allVendors = getAllVendorSortedByLocationID(locationID);

		Promise.all([allVendors]).then(
			(res) => updateStores(res),
			(err) => {
				console.log(err);

				if (!err.isGeneralError) {
					showApiError(err, retrieveFailAlert);
					//history.push(newRoute);
				}
			}
		);
	};

	// Updates relevant stores/context values with API call
	const updateStores = (res) => {
		const allVendors = res[0].data.content;
		// Edit vendors arrays
		editAllVendorList(allVendors);
		editVendorsList(allVendors.vendors);
		editReconVendorsList(allVendors.reconSupplier);
		editFlooringVendorsList(allVendors.flooringVendor);
		editTradeLenderList(allVendors.lenders);

		//edit more information
		editModifiedOn(today);
		editModifiedByID(dcLoginID);
		editOpenedBy(lastName + ", " + firstName);
		editType(inventorySettings.vehicleType);

		dataIsLoaded();
	};

	useEffect(() => {
		getInvDetails();
		return () => {
			reset();
			resetPhotos();
		};
		// eslint-disable-next-line
	}, []);

	// Define form validation parameters using centralized schema
	const methods = useForm({
		reValidateMode: "onBlur",
		resolver: yupResolver(inventoryValidationSchema),
	});

	return (
		<div>
			<Row className="mx-auto content">
				<h3 className="mt-2 mx-auto">
					Add&nbsp;|&nbsp;<span style={{ fontSize: "20px" }}>
						Inventory</span>
				</h3>
			</Row>
			<div className="mx-auto">
				<Card className="card-plain m-0">
					<CardBody className="pt-0">
						<VinDecoder
							header="Decode VIN"
							useGlobalVin={false}
							updateStore={true}
							textInputSize="add"
						/>
					</CardBody>
				</Card>
			</div>
			<MultiImageProvider>
				<KbbValuesProvider>
					<FormProvider {...methods}>
						<InventoryDetailsTab hideDelete="true" />
						<UploadPhotos newVehicle={true} />
						<NewInventorySaveButton />
					</FormProvider>
				</KbbValuesProvider>
			</MultiImageProvider>
		</div>
	);
};

export default InventoryAddPage;
